import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
  IconButton,
  Chip,
  Checkbox
} from "@mui/material";
import {
  Search as SearchIcon,
  PeopleOutline as PeopleIcon,
  ShieldOutlined as ShieldIcon,
  AddCircleOutline as AddIcon,
  CancelOutlined as RemoveIcon,
  ChevronRight as ChevronRightIcon,
  ChevronLeft as ChevronLeftIcon,
  EditNote as EditNoteIcon,
  SentimentSatisfiedAltOutlined as EmojiIcon,
  AttachFile as AttachFileIcon,
  Send as SendIcon
} from "@mui/icons-material";

const avatarColors = [
  { bg: "#E0F2FE", color: "#0369A1" }, // Blue
  { bg: "#FFEDD5", color: "#C2410C" }, // Peach
  { bg: "#F3E8FF", color: "#6B21A8" }, // Purple
  { bg: "#DCFCE7", color: "#15803D" }, // Green
  { bg: "#FEE2E2", color: "#B91C1C" }, // Red
  { bg: "#FEF3C7", color: "#B45309" }, // Amber
];

const getInitials = (name) => {
  if (!name) return "U";
  const parts = name.trim().split(" ");
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

const BulkSMSSelector = ({ passengers, onSendMessage }) => {
  const [left, setLeft] = useState([]);
  const [right, setRight] = useState([]);
  const [checked, setChecked] = useState([]);
  const [bulkMessage, setBulkMessage] = useState("");
  const [leftSearch, setLeftSearch] = useState("");
  const [rightSearch, setRightSearch] = useState("");
  const [selectedType, setSelectedType] = useState("USER");

  const leftChecked = checked.filter((value) => left.includes(value));
  const rightChecked = checked.filter((value) => right.includes(value));

  useEffect(() => {
    if (passengers && passengers.length > 0) {
      const filtered = passengers.filter((p) => p.row.userType === selectedType);
      setLeft(filtered);
      setRight([]);
      setChecked([]);
    } else {
      setLeft([]);
      setRight([]);
      setChecked([]);
    }
  }, [passengers, selectedType]);

  const handleToggle = (value) => () => {
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleMoveRight = (item) => {
    setRight((prev) => [...prev, item]);
    setLeft((prev) => prev.filter((i) => i.row._id !== item.row._id));
    setChecked((prev) => prev.filter((i) => i.row._id !== item.row._id));
  };

  const handleMoveLeft = (item) => {
    setLeft((prev) => [...prev, item]);
    setRight((prev) => prev.filter((i) => i.row._id !== item.row._id));
    setChecked((prev) => prev.filter((i) => i.row._id !== item.row._id));
  };

  const handleAllRight = () => {
    setRight((prev) => prev.concat(left));
    setChecked((prev) => [...prev, ...left]);
    setLeft([]);
  };

  const handleAllLeft = () => {
    setLeft((prev) => prev.concat(right));
    setRight([]);
    setChecked([]);
  };

  const handleCheckedRight = () => {
    setRight((prev) => prev.concat(leftChecked));
    setLeft((prev) => prev.filter((value) => !leftChecked.includes(value)));
    setChecked((prev) => prev.filter((value) => !leftChecked.includes(value)));
  };

  const handleCheckedLeft = () => {
    setLeft((prev) => prev.concat(rightChecked));
    setRight((prev) => prev.filter((value) => !rightChecked.includes(value)));
    setChecked((prev) => prev.filter((value) => !rightChecked.includes(value)));
  };

  const handleSend = () => {
    const selecteddatas = right.map((passenger) => ({
      id: passenger.row._id,
      name: passenger.row.user_name,
      mobile: `${passenger.mobile || passenger.row.mobileNumber || passenger.row.mobile || ""}`
        .replace(/\s+/g, "")
        .replace("+", ""),
    }));

    if (onSendMessage) {
      onSendMessage(selecteddatas, bulkMessage);
    }
    setBulkMessage("");
    setRight([]);
    setLeft(passengers ? passengers.filter((p) => p.row.userType === selectedType) : []);
    setChecked([]);
  };

  const getSubtext = (item) => {
    const mob = item.mobile || item.row?.mobileNumber || item.row?.mobile;
    if (mob) {
      return String(mob).startsWith("+") ? mob : `+${mob}`;
    }
    return "+91 12649765626";
  };

  const filteredLeft = left.filter(
    (item) =>
      item.row?.user_name?.toLowerCase().includes(leftSearch.toLowerCase()) ||
      getSubtext(item).toLowerCase().includes(leftSearch.toLowerCase())
  );

  const filteredRight = right.filter(
    (item) =>
      item.row?.user_name?.toLowerCase().includes(rightSearch.toLowerCase()) ||
      getSubtext(item).toLowerCase().includes(rightSearch.toLowerCase())
  );

  return (
    <Box sx={{ width: "100%", mb: 3 }}>
      {/* Header Bar with Tabs and Breadcrumb */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        {/* Toggle Pills */}
        <Box
          display="flex"
          alignItems="center"
          bgcolor="#F3F4F6"
          borderRadius="12px"
          p="4px"
        >
          <Button
            onClick={() => setSelectedType("USER")}
            sx={{
              borderRadius: "8px",
              px: 2.5,
              py: 0.8,
              fontSize: "14px",
              fontWeight: selectedType === "USER" ? 600 : 500,
              textTransform: "none",
              backgroundColor: selectedType === "USER" ? "#111827" : "transparent",
              color: selectedType === "USER" ? "#FFFFFF" : "#4B5563",
              "&:hover": {
                backgroundColor: selectedType === "USER" ? "#1F2937" : "#E5E7EB",
              },
            }}
          >
            Selected Passenger
          </Button>
          <Button
            onClick={() => setSelectedType("DRIVER")}
            sx={{
              borderRadius: "8px",
              px: 2.5,
              py: 0.8,
              fontSize: "14px",
              fontWeight: selectedType === "DRIVER" ? 600 : 500,
              textTransform: "none",
              backgroundColor: selectedType === "DRIVER" ? "#111827" : "transparent",
              color: selectedType === "DRIVER" ? "#FFFFFF" : "#4B5563",
              "&:hover": {
                backgroundColor: selectedType === "DRIVER" ? "#1F2937" : "#E5E7EB",
              },
            }}
          >
            Selected Driver
          </Button>
        </Box>

        {/* Breadcrumb */}
        <Typography variant="body2" sx={{ color: "#9CA3AF", fontWeight: 500 }}>
          Admin /{" "}
          <Typography
            component="span"
            variant="body2"
            sx={{ color: "#111827", fontWeight: 700, ml: 0.5 }}
          >
            SMS Notification
          </Typography>
        </Typography>
      </Box>

      {/* Main Dual Card Selector Container */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          alignItems: "stretch",
          mb: 4,
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        {/* Left Card: Available Recipients */}
        <Box
          sx={{
            flex: 1,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            borderRadius: "16px",
            p: 2.5,
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Card Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <PeopleIcon sx={{ color: "#111827", fontSize: 22 }} />
              <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "#111827" }}>
                Available {selectedType === "USER" ? "Passenger" : "Driver"}
              </Typography>
            </Box>
            <Button
              onClick={handleAllRight}
              sx={{
                color: "#2563EB",
                fontWeight: 600,
                fontSize: "13px",
                textTransform: "none",
                p: 0,
                minWidth: "auto",
                "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
              }}
            >
              Select All
            </Button>
          </Box>

          {/* Search Box */}
          <TextField
            fullWidth
            size="small"
            placeholder="Search by name or ID..."
            value={leftSearch}
            onChange={(e) => setLeftSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#9CA3AF", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: "#F9FAFB",
                "& fieldset": { borderColor: "#E5E7EB" },
                "&:hover fieldset": { borderColor: "#D1D5DB" },
              },
            }}
          />

          {/* Items List */}
          <Box
            sx={{
              maxHeight: 280,
              height: 280,
              overflowY: "auto",
              pr: 0.5,
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-thumb": { backgroundColor: "#E5E7EB", borderRadius: "4px" },
            }}
          >
            {filteredLeft.length === 0 ? (
              <Typography sx={{ color: "#9CA3AF", fontSize: "13px", textAlign: "center", mt: 4 }}>
                No available users found
              </Typography>
            ) : (
              filteredLeft.map((item, idx) => {
                const colorObj = avatarColors[idx % avatarColors.length];
                const isChecked = checked.includes(item);
                return (
                  <Box
                    key={item.row._id || idx}
                    onClick={handleToggle(item)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 1.2,
                      mb: 1,
                      borderRadius: "10px",
                      backgroundColor: isChecked ? "#F3F4F6" : "#FFFFFF",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                      "&:hover": { backgroundColor: "#F9FAFB" },
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Checkbox
                        checked={isChecked}
                        size="small"
                        sx={{ p: 0.5, color: "#D1D5DB", "&.Mui-checked": { color: "#111827" } }}
                      />
                      <Box
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: "50%",
                          backgroundColor: colorObj.bg,
                          color: colorObj.color,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: "13px",
                          flexShrink: 0,
                        }}
                      >
                        {getInitials(item.row.user_name)}
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#111827", lineHeight: 1.2 }}>
                          {item.row.user_name || "Unnamed"}
                        </Typography>
                        <Typography sx={{ color: "#6B7280", fontSize: "12px" }}>
                          {getSubtext(item)}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveRight(item);
                      }}
                      sx={{ color: "#9CA3AF", "&:hover": { color: "#111827" } }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>

        {/* Transfer Controls Column */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "row", md: "column" },
            justifyContent: "center",
            alignItems: "center",
            gap: 1.5,
            px: 1,
          }}
        >
          <IconButton
            onClick={handleCheckedRight}
            sx={{
              backgroundColor: "#111827",
              color: "#FFFFFF",
              width: 36,
              height: 36,
              boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              "&:hover": { backgroundColor: "#1F2937" },
            }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
          <IconButton
            onClick={handleCheckedLeft}
            sx={{
              backgroundColor: "#E0F2FE",
              color: "#0284C7",
              width: 36,
              height: 36,
              "&:hover": { backgroundColor: "#BAE6FD" },
            }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Right Card: Selected Recipients */}
        <Box
          sx={{
            flex: 1,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            borderRadius: "16px",
            p: 2.5,
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Card Header */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <ShieldIcon sx={{ color: "#111827", fontSize: 22 }} />
              <Typography sx={{ fontWeight: 700, fontSize: "15px", color: "#111827" }}>
                Selected Recipients
              </Typography>
              <Chip
                label={right.length}
                size="small"
                sx={{
                  backgroundColor: "#DBEAFE",
                  color: "#1E40AF",
                  fontWeight: 700,
                  fontSize: "12px",
                  height: 20,
                  borderRadius: "10px",
                }}
              />
            </Box>
            <Button
              onClick={handleAllLeft}
              sx={{
                color: "#EF4444",
                fontWeight: 600,
                fontSize: "13px",
                textTransform: "none",
                p: 0,
                minWidth: "auto",
                "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
              }}
            >
              Clear All
            </Button>
          </Box>

          {/* Search Box */}
          <TextField
            fullWidth
            size="small"
            placeholder="Filter selected..."
            value={rightSearch}
            onChange={(e) => setRightSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#9CA3AF", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                borderRadius: "10px",
                backgroundColor: "#F9FAFB",
                "& fieldset": { borderColor: "#E5E7EB" },
                "&:hover fieldset": { borderColor: "#D1D5DB" },
              },
            }}
          />

          {/* Items List */}
          <Box
            sx={{
              maxHeight: 280,
              height: 280,
              overflowY: "auto",
              pr: 0.5,
              "&::-webkit-scrollbar": { width: "6px" },
              "&::-webkit-scrollbar-thumb": { backgroundColor: "#E5E7EB", borderRadius: "4px" },
            }}
          >
            {filteredRight.length === 0 ? (
              <Typography sx={{ color: "#9CA3AF", fontSize: "13px", textAlign: "center", mt: 4 }}>
                No recipients selected
              </Typography>
            ) : (
              filteredRight.map((item, idx) => {
                const isChecked = checked.includes(item);
                const colorObj = avatarColors[idx % avatarColors.length];
                return (
                  <Box
                    key={item.row._id || idx}
                    onClick={handleToggle(item)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 1.2,
                      mb: 1,
                      borderRadius: "12px",
                      backgroundColor: "#F9FAFB",
                      border: "1px solid #E5E7EB",
                      cursor: "pointer",
                      transition: "all 0.15s ease",
                      "&:hover": { borderColor: "#D1D5DB" },
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <Checkbox
                        checked={isChecked}
                        size="small"
                        sx={{ p: 0.5, color: "#D1D5DB", "&.Mui-checked": { color: "#111827" } }}
                      />
                      <Box
                        sx={{
                          width: 38,
                          height: 38,
                          borderRadius: "50%",
                          backgroundColor: colorObj.bg,
                          color: colorObj.color,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: "13px",
                          flexShrink: 0,
                        }}
                      >
                        {getInitials(item.row.user_name)}
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#111827", lineHeight: 1.2 }}>
                          {item.row.user_name || "Unnamed"}
                        </Typography>
                        <Typography sx={{ color: "#6B7280", fontSize: "12px" }}>
                          {getSubtext(item)}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveLeft(item);
                      }}
                      sx={{ color: "#9CA3AF", "&:hover": { color: "#EF4444" } }}
                    >
                      <RemoveIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      </Box>

      {/* Message Content Container */}
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          border: "1px solid #E5E7EB",
          borderRadius: "16px",
          p: 3,
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          mb: 4,
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <EditNoteIcon sx={{ color: "#111827", fontSize: 24 }} />
          <Typography sx={{ fontWeight: 700, fontSize: "16px", color: "#111827" }}>
            Message Content
          </Typography>
        </Box>

        <Box
          sx={{
            backgroundColor: "#F9FAFB",
            borderRadius: "12px",
            border: "1px solid #E5E7EB",
            p: 2,
          }}
        >
          <TextField
            fullWidth
            multiline
            rows={4}
            variant="standard"
            placeholder="Type your broadcast message here. Use @username to mention or #urgent for priority alerts."
            value={bulkMessage}
            onChange={(e) => setBulkMessage(e.target.value)}
            InputProps={{ disableUnderline: true }}
            sx={{
              "& textarea": { fontSize: "14px", color: "#374151" },
            }}
          />

          <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} pt={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <IconButton size="small" sx={{ color: "#6B7280" }}>
                <EmojiIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" sx={{ color: "#6B7280" }}>
                <AttachFileIcon fontSize="small" />
              </IconButton>
            </Box>
            <Typography sx={{ color: "#9CA3AF", fontSize: "12px" }}>
              {bulkMessage.length} / 1000 characters
            </Typography>
          </Box>
        </Box>

        {/* Send Action */}
        <Box display="flex" justifyContent="flex-end" mt={2.5}>
          <Button
            onClick={handleSend}
            variant="contained"
            disabled={right.length === 0 || bulkMessage.trim() === ""}
            startIcon={<SendIcon />}
            sx={{
              backgroundColor: "#111827",
              color: "#FFFFFF",
              borderRadius: "10px",
              px: 3,
              py: 1.2,
              fontWeight: 600,
              textTransform: "none",
              fontSize: "14px",
              boxShadow: "none",
              "&:hover": { backgroundColor: "#1F2937" },
              "&.Mui-disabled": {
                backgroundColor: "#E5E7EB",
                color: "#9CA3AF",
              },
            }}
          >
            Send Notification
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default BulkSMSSelector;
